/**
 * Auth Helper Utilities
 * 
 * Pure functions extracted from AuthContext.tsx.
 * These functions contain ZERO React code and make ZERO decisions about UX.
 * They are pure data transformations with deterministic outputs.
 * 
 * Per docs/invariants/PHASE_1_CORE.md:
 * - AuthContext determines user existence
 * - Auth is binary (valid session or not)
 * - Profile creation is atomic
 * 
 * EXTRACTION RULES (V2 Plan):
 * - Same inputs, same outputs as original inline code
 * - No new validation layers
 * - No new business logic
 * - categorizeAuthError returns ONLY categories already used in existing logic
 * 
 * RISK MITIGATION (V2):
 * - categorizeAuthError returns only: 'network' | 'auth' | 'unknown'
 * - These categories map 1:1 to existing branching in AuthContext
 * - NO NEW CATEGORIES ALLOWED
 */

// ============================================================================
// TYPES
// ============================================================================

/**
 * Auth error categories - LOCKED to existing implicit categories.
 * 
 * These map directly to the existing branching logic in AuthContext:
 * - 'credentials': Invalid login credentials (don't logout)
 * - 'session': 401/403/PGRST301 errors (session expired)
 * - 'network': Transient network failures
 * - 'unknown': Everything else
 */
export type AuthErrorCategory = 'credentials' | 'session' | 'network' | 'unknown';

// ============================================================================
// PURE FUNCTIONS
// ============================================================================

/**
 * Determine if an error is an auth-related error.
 * 
 * Extracted from AuthContext.tsx handleAuthError (lines 76-111).
 * 
 * @param error - Unknown error object
 * @returns true if error is auth-related
 */
export function isAuthError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  
  const err = error as { code?: string; status?: number; message?: string };
  
  // JWT errors
  if (err.code === 'PGRST301') return true;
  if (err.message?.includes('JWT')) return true;
  
  // HTTP auth errors
  if (err.status === 401 || err.status === 403) return true;
  
  // Invalid credentials
  if (err.message?.includes('Invalid login credentials')) return true;
  
  return false;
}

/**
 * Categorize an auth error into one of the existing categories.
 * 
 * Extracted from AuthContext.tsx handleAuthError (lines 76-111).
 * 
 * IMPORTANT: This returns ONLY categories that already exist in the codebase.
 * The categories map directly to existing if/else branches:
 * - 'credentials' -> "Invalid credentials - NOT logging out"
 * - 'session' -> "Session expired/invalid - dispatching auth-error event"
 * - 'network' -> Transient failure, retry is appropriate
 * - 'unknown' -> Fallback
 * 
 * @param error - Unknown error object
 * @returns Category string
 */
export function categorizeAuthError(error: unknown): AuthErrorCategory {
  if (!error || typeof error !== 'object') return 'unknown';
  
  const err = error as { code?: string; status?: number; message?: string };
  
  // Invalid credentials - don't auto-logout
  if (err.message?.includes('Invalid login credentials')) {
    return 'credentials';
  }
  
  // Session expired or invalid token
  if (err.status === 401 || err.status === 403 || err.code === 'PGRST301') {
    return 'session';
  }
  
  // JWT errors are also session errors
  if (err.message?.includes('JWT')) {
    return 'session';
  }
  
  // Network errors (fetch failures, timeouts)
  if (err.message?.includes('fetch') || 
      err.message?.includes('network') ||
      err.message?.includes('timeout') ||
      err.code === 'NETWORK_ERROR') {
    return 'network';
  }
  
  return 'unknown';
}

/**
 * Determine if a profile fetch should be retried.
 * 
 * Extracted from AuthContext.tsx main auth effect (lines 166-265).
 * 
 * Original logic retries up to 3 times with 1s delay for transient failures.
 * Auth errors should NOT be retried.
 * 
 * @param attempt - Current attempt number (1-indexed)
 * @param maxAttempts - Maximum retry attempts
 * @param error - Error that occurred
 * @returns true if should retry, false otherwise
 */
export function shouldRetryProfileFetch(
  attempt: number,
  maxAttempts: number,
  error: unknown
): boolean {
  // Never retry if we've exhausted attempts
  if (attempt >= maxAttempts) return false;
  
  // Never retry auth errors - they won't succeed
  const category = categorizeAuthError(error);
  if (category === 'session' || category === 'credentials') {
    return false;
  }
  
  // Retry network errors and unknown errors
  return true;
}

/**
 * Extract error message from unknown error object.
 * 
 * Utility function for consistent error message extraction.
 * 
 * @param error - Unknown error object
 * @returns Error message string
 */
export function getErrorMessage(error: unknown): string {
  if (!error) return 'An unknown error occurred';
  
  if (typeof error === 'string') return error;
  
  if (typeof error === 'object') {
    const err = error as { message?: string };
    if (err.message) return err.message;
  }
  
  return 'An authentication error occurred';
}
